// The athlete's v2 commands (spec §13.14), as data the queue can persist and
// replay. Each says how to send it and what the calendar should look like
// until the backend confirms it (`op`, read by overlay.js). The queue adds the
// Idempotency-Key and, for plan changes, the base_version the athlete saw.
// `subject` names what a failure note talks about (failures.js).

const v = (body) => ({ ...body });
/** The empty-plan offer's first empty day, as the rebuild / fill body ({} for anything else). */
const fromOf = (item) => (typeof item?.body?.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.body.from) ? { from: item.body.from } : {});

export const commands = {
  move: (session, to) => ({
    kind: 'move', method: 'POST', path: `/v2/sessions/${session.id}/move`, body: v({ to }), versioned: true,
    label: `Move ${session.title || 'session'}`, subject: session.title || 'this session', op: { kind: 'move', id: session.id, to },
  }),
  edit: (session, fields) => ({
    kind: 'edit', method: 'PATCH', path: `/v2/sessions/${session.id}`, body: v({ fields }), versioned: true,
    label: `Edit ${session.title || 'session'}`, subject: session.title || 'this session', op: { kind: 'edit', id: session.id, fields },
  }),
  // Hand a session the athlete made theirs (by editing it) back to the coach
  // to manage again (POST /v2/sessions/{id}/release): one change with Undo.
  release: (session) => ({
    kind: 'release', method: 'POST', path: `/v2/sessions/${session.id}/release`, body: v({}), versioned: true,
    label: `Hand ${session.title || 'session'} back`, subject: session.title || 'this session',
    op: { kind: 'release', id: session.id },
  }),
  skip: (session, reason = null) => ({
    kind: 'skip', method: 'POST', path: `/v2/sessions/${session.id}/skip`, body: v({ reason }), versioned: true,
    label: `Skip ${session.title || 'session'}`, subject: session.title || 'this session', op: { kind: 'skip', id: session.id },
  }),
  // Tick a session done, or un-tick it (POST /v2/sessions/{id}/done): one
  // change with Undo, in place of v1's toggle-done.
  done: (session, done = true) => ({
    kind: 'done', method: 'POST', path: `/v2/sessions/${session.id}/done`, body: v({ done: Boolean(done) }), versioned: true,
    label: done ? `Mark ${session.title || 'session'} done` : `Un-tick ${session.title || 'session'}`,
    subject: session.title || 'this session', op: { kind: 'done', id: session.id, done: Boolean(done) },
  }),
  // Two sessions trade days in ONE change (POST /v2/sessions/swap): both
  // move, or neither does (two moves could half-apply).
  swap: (a, b) => ({
    kind: 'swap', method: 'POST', path: '/v2/sessions/swap', body: v({ a: a.id, b: b.id }), versioned: true,
    label: `Swap ${a.title || 'session'} and ${b.title || 'session'}`, subject: a.title || 'this session',
    op: { kind: 'swap', a: { id: a.id, to: b.date, title: a.title || 'this session' },
      b: { id: b.id, to: a.date, title: b.title || 'that session' } },
  }),
  // "This was my Thursday tempo" (a session), or "it was extra" (null):
  // POST /v2/activities/{activity_uuid}/link {session_id}, not versioned.
  // The activity is its v2 id (strava_activities.id, a uuid: /activities/user's
  // `activity_uuid`, or the v2 store's activity `id`), not the Strava id;
  // `strava_activity_id` is kept for the overlay (GET /plan rows link by it).
  // Same shape as the mobile app's lib/v2/commands.ts.
  link: (activity, session) => ({
    kind: 'link', method: 'POST', path: `/v2/activities/${activity.id}/link`, body: v({ session_id: session ? session.id : null }),
    label: session ? `Link ${activity.name || 'activity'} to ${session.title || 'session'}` : `Mark ${activity.name || 'activity'} as extra`,
    subject: session ? session.title || 'this session' : activity.name || 'that activity',
    op: { kind: 'link', activity_id: activity.id,
      strava_activity_id: activity.strava_activity_id != null ? String(activity.strava_activity_id) : null,
      session_id: session ? session.id : null },
  }),
  remove: (session) => ({
    kind: 'remove', method: 'DELETE', path: `/v2/sessions/${session.id}`, body: v({}), versioned: true,
    label: `Remove ${session.title || 'session'}`, subject: session.title || 'this session', op: { kind: 'remove', id: session.id },
  }),
  // A planned session of a week the coach hasn't written yet (outline.js):
  // `s` is { weekId, plannedId, title }. Moves stay within its Monday–Sunday week.
  removeOutline: (s) => ({
    kind: 'outline_remove', method: 'POST', path: `/v2/outline/${s.weekId}/sessions/${s.plannedId}/remove`, body: v({}), versioned: true,
    label: `Remove ${s.title || 'session'}`, subject: s.title || 'this session', op: { kind: 'outline_remove', id: s.plannedId },
  }),
  moveOutline: (s, to) => ({
    kind: 'outline_move', method: 'POST', path: `/v2/outline/${s.weekId}/sessions/${s.plannedId}/move`, body: v({ to }), versioned: true,
    label: `Move ${s.title || 'session'}`, subject: s.title || 'this session', op: { kind: 'outline_move', id: s.plannedId, to },
  }),
  // "I can't train this day": a training event the coach re-plans that week around.
  markUnavailable: (date, detail = null) => ({
    kind: 'event_add', method: 'POST', path: '/v2/events',
    body: v({ type: 'unavailable', starts_on: date, ends_on: date, ...(detail ? { detail } : {}) }),
    label: "Can't train", subject: 'that day', op: null,
  }),
  // Any sport word (yoga, walk…: the backend keeps it and answers
  // `sport_mapped`); `minutes` unless `steps` (session notation) or a
  // `segment`. `then`: what to do to the new session once it exists (its id
  // is in the answer's session_ids): { fields } an edit (what was done, the
  // RPE), { done: true } tick it off. Data, so the queue can persist it.
  create: ({ date, sport, title, minutes = null, effort_rpe = 4, time_of_day = null, distance_m = null,
    description = null, steps = null, segment = null, environment = null, then = null }) => ({
    kind: 'create', method: 'POST', path: '/v2/sessions',
    body: v({ date, sport, title, ...(minutes ? { minutes } : {}), effort_rpe, time_of_day, ...(distance_m ? { distance_m } : {}),
      ...(description ? { description } : {}), ...(steps ? { steps } : {}), ...(segment ? { segment } : {}),
      ...(environment ? { environment } : {}) }), versioned: true,
    label: `Add ${title}`, subject: title || 'this session', op: { kind: 'create', date, sport, title, minutes },
    ...(then && then.length ? { then } : {}),
  }),

  // A rest day (POST /v2/days/{date}/rest): `clear` takes the day's planned
  // sessions off in the same change (one Undo brings them back). Without it,
  // a day with planned sessions is refused (409 with their session_ids).
  restDay: (date, { clear = false, note = null } = {}) => ({
    kind: 'rest_add', method: 'POST', path: `/v2/days/${date}/rest`, body: v({ clear: Boolean(clear), ...(note ? { note } : {}) }),
    versioned: true, label: 'Rest day', subject: date, op: { kind: 'rest_add', id: date, date, clear: Boolean(clear) },
  }),
  // The day is a training day again (DELETE /v2/days/{date}/rest; 404: it wasn't one).
  clearRest: (date) => ({
    kind: 'rest_remove', method: 'DELETE', path: `/v2/days/${date}/rest`, body: v({}), versioned: true,
    label: 'Remove rest day', subject: date, op: { kind: 'rest_remove', id: date, date },
  }),
  // A session becomes a race (POST /v2/sessions/{id}/race), or a race a
  // session again (POST /v2/races/{id}/session). Each one change with Undo.
  toRace: (session, { priority, details = {} }) => ({
    kind: 'race_on', method: 'POST', path: `/v2/sessions/${session.id}/race`, body: v({ priority, details }), versioned: true,
    label: `Make ${session.title || 'session'} a race`, subject: session.title || 'this session',
    op: { kind: 'race_on', id: session.id, priority },
  }),
  toSession: (race, { minutes = null, effort_rpe = null } = {}) => ({
    kind: 'race_off', method: 'POST', path: `/v2/races/${race.id}/session`,
    body: v({ ...(minutes ? { minutes } : {}), ...(effort_rpe ? { effort_rpe } : {}) }), versioned: true,
    label: `Make ${race.title || 'race'} a session`, subject: race.title || 'this race', op: { kind: 'race_off', id: race.id },
  }),
  // A race edited in the session modal (PATCH /v2/races, M4): the calendar
  // re-reads it when the stream carries race.upsert, so nothing is overlaid.
  addRace: ({ date, title, sport, priority = null, details = {} }) => ({
    kind: 'race_add', method: 'POST', path: '/v2/races',
    body: v({ date, title, sport, ...(priority ? { priority } : {}), details }),
    label: `Add ${title || 'race'}`, op: null,
  }),
  editRace: (race, fields) => ({
    kind: 'race_edit', method: 'PATCH', path: `/v2/races/${race.id}`, body: v(fields),
    label: `Edit ${race.title || 'race'}`, subject: race.title || 'this race', op: null,
  }),
  closeEdits: () => ({ kind: 'close_edits', method: 'POST', path: '/v2/edits/close', body: {}, label: 'Done editing', op: null }),

  addNote: ({ start_date, end_date = null, text }) => ({
    kind: 'note_add', method: 'POST', path: '/v2/notes', body: v({ start_date, end_date, text }),
    label: 'Add note', op: { kind: 'note_add', start_date, end_date, text },
  }),
  editNote: (note, { start_date, end_date = null, text }) => ({
    kind: 'note_edit', method: 'PATCH', path: `/v2/notes/${note.id}`, body: v({ start_date, end_date, text }),
    label: 'Edit note', op: { kind: 'note_edit', id: note.id, start_date, end_date, text },
  }),
  deleteNote: (note) => ({
    kind: 'note_delete', method: 'DELETE', path: `/v2/notes/${note.id}`, body: null,
    label: 'Delete note', op: { kind: 'note_delete', id: note.id },
  }),

  clearChat: () => ({
    kind: 'clear_chat', method: 'POST', path: '/v2/thread/clear', body: {}, label: 'Clear chat', op: { kind: 'clear_chat' },
  }),
  // `chip`: the message is a tapped chip (v2/suggestions.js); the backend keeps it on the item, which logs the tap.
  message: (text, context_ref = null, chip = null) => ({
    kind: 'message', method: 'POST', path: '/v2/thread/messages',
    body: v({ text, ...(context_ref ? { context_ref } : {}), ...(chip ? { chip } : {}) }),
    label: 'Send message', op: { kind: 'message', text, context_ref },
  }),
  acceptProposal: (item) => ({
    kind: 'accept', method: 'POST', path: `/v2/proposals/${item.id}/accept`, body: {}, label: 'Accept', op: { kind: 'item_state', id: item.id, state: 'accepted' },
  }),
  // A stale proposal's Refresh (body.stale, actions 'refresh'): the backend
  // renews it when only the clock moved, else the coach writes it again (202
  // {refreshed: renewed | rebuilding}); POST /v2/proposals/{id}/refresh.
  refreshProposal: (item) => ({
    kind: 'refresh', method: 'POST', path: `/v2/proposals/${item.id}/refresh`, body: {}, label: 'Refresh',
    op: { kind: 'item_state', id: item.id, state: 'refreshing' },
    // the backend will write the plan again (a full rebuild): rebuildConfirm asks first after one today
    rebuilds: Boolean(item.body?.refresh_rebuilds),
  }),
  dismissProposal: (item) => ({
    kind: 'dismiss', method: 'POST', path: `/v2/proposals/${item.id}/dismiss`, body: {}, label: 'Dismiss', op: { kind: 'item_state', id: item.id, state: 'dismissed' },
  }),
  answerIntake: (item, answer, text = null) => ({
    kind: 'intake_answer', method: 'POST', path: `/v2/intake/${item.id}/answer`,
    body: v({ answer, ...(text && text.trim() ? { text: text.trim() } : {}) }),
    label: 'Answer', op: { kind: 'intake_answer', id: item.id, answer },
  }),
  // A coach message that says a plan couldn't be built offers Try again
  // (body.actions 'rebuild'): the coach builds it again (202 {command_id, seq}).
  // The calendar's own build names itself calendarRoutes.PLAN_BUILD_ID.
  // An empty calendar's offer names its first empty day (body.from): the new plan is written from it.
  rebuildPlan: (item) => ({
    kind: 'rebuild', method: 'POST', path: '/v2/plan/rebuild', body: fromOf(item), label: 'Rebuild plan', op: { kind: 'rebuild', id: item.id },
  }),
  // An empty calendar with a plan whose outline covers it (body.actions 'fill'): the coach writes
  // the empty days from the plan, from body.from, applied with Undo (202 {command_id, mode, seq};
  // mode 'rebuild' when the outline no longer covers them and a new plan is drafted instead).
  fillPlan: (item) => ({
    kind: 'fill', method: 'POST', path: '/v2/plan/fill', body: fromOf(item), label: 'Fill plan', op: { kind: 'fill', id: item.id },
  }),
  // changeId: the item's refs.change_id (threadView.changeIdOf).
  undo: (item, changeId) => ({
    kind: 'undo', method: 'POST', path: `/v2/changes/${changeId}/undo`, body: {}, label: 'Undo', op: { kind: 'item_state', id: item.id, state: 'undone' },
  }),
};

